// Paper-size options + layout config for Visiting Card (certificate_type._id === 1) printing.
// Single source of truth shared by the print handlers (usePrintType.js) and the
// visiting-card templates (NewFrontCard / Back / NewVisitingDuplex) so the dropdown,
// the per-sheet card count, the container geometry, and the jsPDF page format never drift.
//
// `paper_size` is purely client-side — it is NOT sent in the PrintCertficate payload.
// It only controls how many cards are laid out per sheet and which PDF page size is used.
//
// A4's page geometry (containerWidth 720 <-> 210mm, pageHeight 1113.5, 2x5 grid) is the
// battle-tested baseline every other size is derived from — don't change those. Its gaps and
// paddings DID move once, when A4 got the same cut-margin/bleed fix as A3/A5 (see config below).
//
// pageHeight = one PDF page expressed in the template's px. The print handler uses
// margin [0,9,0,9] (9mm each side), so html2pdf fits containerWidth to the page's INNER
// width (pageWidth - 18mm) and that scale sets the page height:
//     pageHeight = pageHeightMM * containerWidth / (pageWidthMM - 18)
//   A4: 297 * 720    / (210-18) = 1113.75 (~1113.5)
//   A3: 297 * 1507.5 / (420-18) = 1113.75 (~1113.5; A3 landscape: pageWidth 420, pageHeight 297)
//   A5: 148 * 720    / (210-18) = 555     (A5 landscape: pageWidth 210, pageHeight 148)
// containerWidth is the sheet's printable width in the same px scale (A4 720 <-> 210mm).
// A printed proof is still the final word — tune chunkSize / containerWidth / pageHeight here.

export const PAPER_SIZES = [
  { name: "A3", id: 1 },
  { name: "A4", id: 2 },
  { name: "A5", id: 3 },
];

export const DEFAULT_PAPER_SIZE_ID = 2; // A4

export const DEFAULT_PAPER_SIZE = { name: "A4", id: DEFAULT_PAPER_SIZE_ID };

// The front card content box is 321x207px. The BACK of each card is printed 3% larger in
// every direction (330.63x213.21) -- a deliberate full-bleed overscan so that when the
// client hand-cuts to ATM size the back image runs past the cut on all sides with NO white
// slivers. The front, by contrast, carries its cut-margin INSIDE its footprint: the card box
// widens by 2*cardSideMargin so the colored header/footer/bg bleed to the cut edge while the
// text body is inset by the same amount (handled in the template, NOT a wrapper). So the back
// must always stay 3% wider than the front's cut FOOTPRINT.
//
// cardTopMargin is the same idea on the TOP edge only (the bottom already bleeds): the card box
// grows by cardTopMargin ABOVE the cut line and the whole design (header band included) is pushed
// down by the same px, so the added strip is filled by the card's stretched background image and
// nothing in the artwork moves or distorts. The card is cut at 207px from the box's BOTTOM.
//
// When the front gains side margin (cardSideMargin) its footprint widens (321 -> 321+2*pad),
// so the back has to widen by the same 3% to preserve the overscan -- otherwise the back ends
// up the same size as (or narrower than) the front and the bleed is lost. buildConfig derives
// the back card size from the (possibly padded) front footprint, then shrinks the back gaps by
// exactly the back's extra size so the column/row PITCH still equals the front's and each back
// lands directly behind its front for duplex ([1 2 3 4] front, row-reversed to [4 3 2 1] back).
const FRONT_CARD_WIDTH = 321;
const FRONT_CARD_HEIGHT = 207;
const BACK_BLEED_FACTOR = 330.63 / FRONT_CARD_WIDTH; // 1.03 -- back is 3% larger than the front footprint
const CARD_BORDER = 1; // 1px solid edge on both the front cut-guide and the back card box

const round = (n) => Math.round(n * 1e4) / 1e4; // strip float noise (e.g. 52.370000000000005 -> 52.37)

// `front` = spacing OUTSIDE the cards (gaps between cards + page padding). The card
// itself and everything inside it are unaffected. EDIT ONLY THE FRONT BLOCK — the
// back column/row gaps are computed from it (see buildConfig) so the back stays
// pitch-locked to the front automatically. `backPadding` is the only back value set
// by hand: the page padding that anchors the mirrored grid. Because the back row is
// right-anchored (row-reverse), its `paddingRight` is what slides the whole back grid
// sideways over the front — recheck it whenever a `front` gap or `cardSideMargin` moves.
// Tuning a size = change its `front` gaps / `containerWidth` / `pageHeight` here.
const RAW_PAPER_SIZE_CONFIG = {
  // A3 landscape (420x297mm): 4 cols x 5 rows = 20 cards/sheet.
  // Height 297mm == A4, so pageHeight reuses A4's 1113.5 and rows match A4 (rowGap 14, 5 rows).
  // Width 420mm -> containerWidth 1507.5 (= (420-18)mm inner / A4's px scale) so cards stay A4-size.
  // columnGap 67 fits exactly 4 cards across: 4*321 + 3*67 + 16 = 1501 <= 1507.5.
  1: {
    key: "a3",
    format: "a3",
    orientation: "landscape",
    chunkSize: 20,
    containerWidth: 1507.5,
    pageHeight: 1113.5,
    // cardSideMargin = L/R cut-margin (px) baked into each FRONT card: the card box widens by
    // 2*cardSideMargin (colored header/footer/bg then bleed to the cut edge) and the text body is
    // inset by the same px, so hand-cutting to ATM size doesn't clip content or congest the sides.
    // The back card widens by the same 3% (buildConfig,
    // via BACK_BLEED_FACTOR) so it stays the wider full-bleed surface, and the back gap shrinks to
    // keep the pitch locked. columnGap 46 keeps 4 cols across with the padded front cells.
    cardSideMargin: 5,
    // backBleedX = extra horizontal bleed per side (px) the back runs past the front's L/R cut
    // line, ON TOP of the 3% overscan. Raise it to clip more of the back when hand-cutting; the
    // back column gap auto-shrinks to keep the pitch locked. (front 331 footprint -> back 346.93.)
    backBleedX: 3,
    // rowGap 6 (tight rows). Centered paddingTop would be (1113.5 - (5*207 + 4*6))/2 = 27.25;
    // it's bumped to 33.25 to nudge the whole grid ~1.6mm DOWN. paddingLeft 14 (was 8) nudges it
    // ~1.6mm RIGHT. NOTE: this moves the FRONT only (and the duplex FRONT half); the duplex BACK
    // still uses backPadding below, so A3 DUPLEX front/back no longer perfectly register — bump
    // backPadding.paddingTop by the same +6 (and shift the back right) if duplex alignment is needed.
    front: { columnGap: 46, rowGap: 6, paddingTop: 33.25, paddingLeft: 14, paddingRight: 8 },
    // back is right-anchored (row-reverse), so paddingRight is the lever that shifts the whole
    // back grid left/right; paddingLeft is inert (slack piles up on the left). Raising paddingRight
    // trims the back's left-side margin AND its left bleed over the front cut line by the same px.
    backPadding: { paddingTop: 24.15, paddingLeft: 4.5, paddingRight: 4.5 },
  },
  // A4 portrait (210x297mm): 2 cols x 5 rows = 10 cards/sheet.
  // cardSideMargin 5 + backBleedX 2 give A4 the same white-sliver fix A3/A5 already have: the
  // front card widens 321 -> 331 so its colour bleeds to the cut line, and the back grows to
  // 344.93 so it still overhangs the front cut by ~6.97px (~1.85mm) per side.
  // columnGap 62 -> 40 pays for the wider front cells: 8 + 2*331 + 40 + 8 = 718 <= 720.
  // The back is right-anchored (row-reverse), so paddingRight is the lever that centres it over
  // the front: back row = 2*344.93 + 26.07 = 715.93 wide, right edge 720 - 3.04 = 716.96, left
  // edge 1.03 -- i.e. 6.97/6.96px of bleed either side of the front's 8..710 span. paddingLeft 1
  // is just the leftover slack (inert on a row-reverse row) and must stay <= 1.03, or the back
  // row overflows 720 and wraps to one card per line.
  // cardTopMargin 5 then adds the same bleed on the TOP edge (card box 207 -> 212, the extra 5px
  // sitting ABOVE the cut line). paddingTop 12 -> 7 and rowGap 14 -> 9 keep the row PITCH at
  // 207+14 = 212+9 = 221 and put every cut line exactly where it already prints, so the proofed
  // sheet geometry is unchanged -- the cards just gain 5px of red above each cut. Total height is
  // invariant too: 7 + 5*212 + 4*9 = 1103, same as 12 + 5*207 + 4*14.
  // back paddingTop 8.5 -> 6.32 re-centres the (now 218.36 tall) back on the front's 12..219 cut
  // window; the back row gap is derived, so the back pitch stays 221 and duplex stays registered.
  2: {
    key: "a4",
    format: "a4",
    orientation: "portrait",
    chunkSize: 10,
    containerWidth: 720,
    pageHeight: 1113.5,
    cardSideMargin: 5,
    cardTopMargin: 5,
    backBleedX: 2,
    front: { columnGap: 40, rowGap: 9, paddingTop: 7, paddingLeft: 8, paddingRight: 8 },
    backPadding: { paddingTop: 6.32, paddingLeft: 1, paddingRight: 3.04 },
  },
  // A5 landscape (210x148mm): half of A4 -> 4 cards/sheet (2 cols x 2 rows).
  // Same width as A4 portrait, so containerWidth/columns/front gaps match A4.
  // pageHeight = 148 * 720 / (210 - 18) = 555 (one A5 landscape page; see formula above).
  3: {
    key: "a5",
    format: "a5",
    orientation: "landscape",
    chunkSize: 4,
    containerWidth: 720,
    // pageHeight 555 = exactly one A5 page (148*720/192), so every FULL page maps 1:1 to a
    // physical PDF page with ZERO vertical drift (identical top padding on every page). The
    // snag: an exact N*555 total lands on html2pdf's Math.ceil page-count boundary, where
    // float/sub-pixel rounding intermittently emits a trailing BLANK page. Fix: trim ONLY the
    // final page (lastPageTrimPerPage * page count), pushing the total just under that boundary
    // (no blank page) while every other page stays 555 — so multi-page jobs DON'T creep upward.
    // (Shrinking pageHeight itself, e.g. 553, makes EVERY page short, so the canvas slices at
    // 555 but each chunk is 553 => content drifts up page-by-page and the last page looks
    // under-padded. That's why we trim only the last page, not all of them.)
    // The trim SCALES with page count (lastPageTrimPerPage * pages) because html2canvas's
    // sub-pixel height excess accumulates per page, so a flat trim runs out on big jobs (e.g. 60
    // cards) and the blank page returns. A5 is verticalCenter, so the trimmed strip on the last
    // page is invisible. Components compute this for the final chunk (NewFrontCard / NewVisitingDuplex).
    pageHeight: 555,
    lastPageTrimPerPage: 0.3,
    // Only A5 centers vertically (2 rows leave lots of slack). A3/A4 stay top-aligned.
    // When verticalCenter is set, paddingTop is ignored (centering replaces it).
    verticalCenter: true,
    // White L/R padding on the FRONT card only (see A3). columnGap 32 keeps 2 cols with padded cells.
    cardSideMargin: 5,
    // Extra bleed per side past the front cut line, on top of the 3% (see A3). backBleedY adds
    // top/bottom height; the back row gap auto-shrinks to keep the vertical pitch locked.
    backBleedX: 3,
    backBleedY: 2,
    front: { columnGap: 32, rowGap: 14, paddingTop: 12, paddingLeft: 8, paddingRight: 8 },
    // A5 is vertically centered, so paddingTop nudges the centered back DOWN by paddingTop/2 (NOT
    // from the top edge). Dropping the back trades top bleed for bottom bleed, so keep paddingTop/2
    // below backBleedY's top bleed or the back's top edge stops covering the front.
    backPadding: { paddingTop: 4, paddingLeft: 4.5, paddingRight: 1.87 },
  },
};

// Assemble the final `back` block: gaps derived from the front (pitch-locked),
// paddings taken from `backPadding`.
const buildConfig = ({ backPadding, ...cfg }) => {
  const frontPad = cfg.cardSideMargin || 0;
  const frontTopPad = cfg.cardTopMargin || 0;

  // Front cut footprint = the (widened/heightened) front card box, edge to edge. cardSideMargin
  // adds to both sides; cardTopMargin adds to the top only.
  const frontFootprintWidth = FRONT_CARD_WIDTH + 2 * frontPad;
  const frontFootprintHeight = FRONT_CARD_HEIGHT + frontTopPad;

  // Back card = front footprint scaled up by the 3% bleed (full-bleed overscan), plus optional
  // EXTRA bleed per side: backBleedX (px, L/R) and backBleedY (px, top/bottom) so the back runs
  // further past the front's cut line -- cutting along the front then clips a little of the back
  // instead of leaving a white sliver. A size with no padding/bleed at all -> 330.63x213.21.
  const backBleedX = cfg.backBleedX || 0;
  const backBleedY = cfg.backBleedY || 0;
  const backCardWidth = round(frontFootprintWidth * BACK_BLEED_FACTOR + 2 * backBleedX);
  const backCardHeight = round(frontFootprintHeight * BACK_BLEED_FACTOR + 2 * backBleedY);

  // Outer (border-box) sizes drive the pitch: gaps shrink by exactly the back card's extra
  // size so the back column/row PITCH equals the front's and each back lands behind its front.
  const frontCellWidth = frontFootprintWidth + 2 * CARD_BORDER;
  const frontCellHeight = frontFootprintHeight + 2 * CARD_BORDER;
  const backCellWidth = backCardWidth + 2 * CARD_BORDER;
  const backCellHeight = backCardHeight + 2 * CARD_BORDER;

  return {
    ...cfg,
    back: {
      // The back card box (Back.js / NewVisitingDuplex.js read these instead of hardcoding).
      cardWidth: backCardWidth,
      cardHeight: backCardHeight,
      // Gaps smaller than the front by the back card's extra size (pitch-locked). Clamp at 0:
      // a tight front rowGap can make this negative (back card taller than the front pitch),
      // and CSS gaps can't be negative.
      columnGap: Math.max(0, round(cfg.front.columnGap - (backCellWidth - frontCellWidth))),
      rowGap: Math.max(0, round(cfg.front.rowGap - (backCellHeight - frontCellHeight))),
      ...backPadding,
    },
  };
};

export const PAPER_SIZE_CONFIG = {
  1: buildConfig(RAW_PAPER_SIZE_CONFIG[1]),
  2: buildConfig(RAW_PAPER_SIZE_CONFIG[2]),
  3: buildConfig(RAW_PAPER_SIZE_CONFIG[3]),
};

// Resolve a config from a paper_size id, falling back to A4 for any unknown/empty value.
export const getPaperConfig = (id) =>
  PAPER_SIZE_CONFIG[id] ?? PAPER_SIZE_CONFIG[DEFAULT_PAPER_SIZE_ID];

// Max number of items (certificates) allowed in a single Card-Certificate print, by paper size
// and side, to keep each PDF within a safe page/memory budget. `single` = front OR back (1 page
// per sheet); `duplex` = front + back (2 pages per sheet, so half as many items fit).
// Only A3/A4/A5 are capped; CR80 (id 4) and unlisted sizes have no limit.
export const PRINT_ITEM_LIMITS = {
  1: { single: 160, duplex: 80 }, // A3
  2: { single: 140, duplex: 70 }, // A4
  3: { single: 116, duplex: 56 }, // A5
};

// side: 1 = front, 2 = back, 3 = duplex. Returns the max item count for a paper size + side,
// or null when no limit applies (CR80 / unknown size).
export const getPrintItemLimit = (paperSizeId, sideId) => {
  const limits = PRINT_ITEM_LIMITS[paperSizeId];
  if (!limits) return null;
  return sideId === 3 ? limits.duplex : limits.single;
};
